import { Button, Modal, Select } from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { sendInvitation } from "../../../utils/invitation";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";

export type InvitationProduct = "bp" | "planung";

const PRODUCT_LABEL: Record<InvitationProduct, string> = {
  bp: "Bildungsplattform",
  planung: "TK-Planung",
};
const PRODUCT_OPTIONS: SelectOptionType[] = (["bp", "planung"] as const).map((value) => ({
  value,
  label: PRODUCT_LABEL[value],
}));

type Props = {
  /** The invitation is sent per profile, not per account. */
  profileUuid: string;
  email: string;
  userName: string;
  /**
   * Products the profile can actually reach. With one (or none) there is
   * nothing to choose; the dialog only confirms.
   */
  products: readonly InvitationProduct[];
  /** URL of each product's application; sent along so the invitation leads back into that tool. */
  productUrls?: Partial<Record<InvitationProduct, string>>;
  /** Preselected product, e.g. derived from the previous invitation's frontend URL. */
  defaultProduct?: InvitationProduct | null;
  onClose: () => void;
  onSent: () => void;
};

/**
 * Confirm + send a (re-)invitation; the product is asked only when the profile
 * can reach more than one.
 */
export function ResendInvitationModal({
  profileUuid,
  email,
  userName,
  products,
  productUrls,
  defaultProduct,
  onClose,
  onSent,
}: Props) {
  const choice = products.length > 1;
  const initial =
    defaultProduct && products.includes(defaultProduct) ? defaultProduct : (products[0] ?? "bp");
  const [product, setProduct] = useState<string>(initial);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      await sendInvitation(
        profileUuid,
        email,
        product,
        productUrls?.[product as InvitationProduct],
      );
      showSuccessToast(`Einladung an ${email} erneut gesendet`);
      onSent();
    } catch (error) {
      showUserErrorToast({ text: "Einladung konnte nicht gesendet werden", error });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      title={`Einladung erneut senden — ${userName}`}
      onRequestClose={onClose}
    >
      <p style={{ margin: "0 0 14px", fontSize: "var(--font-size-s)", color: "var(--color-grey)" }}>
        Eine neue Einladung
        {choice ? "" : ` für ${PRODUCT_LABEL[product as InvitationProduct] ?? product}`} wird an{" "}
        <strong>{email}</strong> gesendet.
      </p>
      {choice && (
        <Select
          name="inviteProduct"
          label="Produkt"
          options={PRODUCT_OPTIONS}
          value={PRODUCT_OPTIONS.find((o) => o.value === product) ?? null}
          isSearchable={false}
          onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) =>
            setProduct(String((opt as SelectOptionType).value))
          }
        />
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "var(--spacing-2)",
          marginTop: "var(--spacing-4)",
        }}
      >
        <Button hierarchy="tertiary" onClick={onClose}>
          Abbrechen
        </Button>
        <Button hierarchy="primary" disabled={busy} onClick={() => void send()}>
          Senden
        </Button>
      </div>
    </Modal>
  );
}
